import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThreeDots } from 'react-loader-spinner';
import DefaultLayout from '../../reusableComponents/defaultLayout';
import AccountHeader, { subContractorTabs } from '../../components/account/AccountHeader';
import SummaryCard from '../../components/dashboard/SummaryCard';
import BidPipeline from '../../components/dashboard/BidPipeline';
import ActiveProjectsOverview from '../../components/dashboard/ActiveProjectsOverview';
import OpportunityFinder from '../../components/dashboard/OpportunityFinder';
import RecentBidResults from '../../components/dashboard/RecentBidResults';
import { API_BASE_URL } from '../../config/api';

// Icons
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import EventIcon from '@mui/icons-material/Event';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import ListAltIcon from '@mui/icons-material/ListAlt';

export default function SubContractorDashboard() {
    const navigate = useNavigate();
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        setLoading(true);
        const token = localStorage.getItem('authToken');
        
        try {
            const response = await fetch(`${API_BASE_URL}/dashboard_summary`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setDashboardData(data.data);
                } else {
                    setError('Failed to load dashboard data');
                }
            } else {
                setError('Failed to load dashboard data');
            }
        } catch (err) {
            setError('An error occurred while loading dashboard data');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <DefaultLayout>
                <AccountHeader activeTab="dashboard" tabs={subContractorTabs} />
                <section className="dashboard_main_content">
                    <div className="container">
                        <div className="dashboard_loading">
                            <ThreeDots
                                height="60"
                                width="80"
                                radius="9"
                                color="#B1903F"
                                ariaLabel="three-dots-loading"
                                visible={true}
                            />
                            <p>Loading dashboard...</p>
                        </div>
                    </div>
                </section>
            </DefaultLayout>
        );
    }

    const summary = dashboardData?.summary || {};

    return (
        <DefaultLayout>
            <AccountHeader activeTab="dashboard" tabs={subContractorTabs} />

            {/* Summary Cards */}
            <section className="dashboard_main_content">
                <div className="container">
                    <div className="dashboard_summary_cards">
                        <SummaryCard
                            title="Pending Bids"
                            value={summary.pending_bids || 0}
                            icon={HourglassEmptyIcon}
                            subtitle="Awaiting decision"
                            onClick={() => navigate('/account/1')}
                        />
                        <SummaryCard
                            title="Active Projects"
                            value={summary.active_projects || 0}
                            icon={TrendingUpIcon}
                            subtitle="Currently working on"
                            onClick={() => navigate('/account/2')}
                        />
                        <SummaryCard
                            title="Win Rate"
                            value={`${summary.win_rate || 0}%`}
                            icon={EmojiEventsIcon}
                            subtitle={`${summary.won_bids || 0} of ${summary.total_bids_submitted || 0} bids won`}
                        />
                        <SummaryCard
                            title="Upcoming Deadlines"
                            value={summary.upcoming_deadlines || 0}
                            icon={EventIcon}
                            subtitle="Projects ending soon"
                            onClick={() => navigate('/account/3')}
                        />
                    </div>

                    {/* Main Dashboard Grid */}
                    <div className="dashboard_grid">
                        <BidPipeline bids={dashboardData?.bid_pipeline} />
                        <ActiveProjectsOverview 
                            projects={dashboardData?.active_work} 
                            title="Active Work"
                        />
                    </div>

                    {/* Opportunities and Results */}
                    <div className="dashboard_grid" style={{ marginTop: '24px' }}>
                        <OpportunityFinder opportunities={dashboardData?.opportunities} />
                        <RecentBidResults results={dashboardData?.recent_bid_results} />
                    </div>

                    {/* Quick Actions */}
                    <div className="dashboard_section" style={{ marginTop: '24px' }}>
                        <div className="section_header">
                            <h3>Quick Actions</h3>
                        </div>
                        <div className="dashboard_quick_actions" style={{ borderTop: 'none', paddingTop: 0, marginTop: 0 }}>
                            <button 
                                className="quick_action_btn primary"
                                onClick={() => navigate('/find-a-project')}
                            >
                                <SearchIcon /> Find Projects
                            </button>
                            <button 
                                className="quick_action_btn"
                                onClick={() => navigate('/account/1')}
                            >
                                <ListAltIcon /> View Pending Bids
                            </button>
                            <button 
                                className="quick_action_btn"
                                onClick={() => navigate('/account/0')}
                            >
                                <PersonIcon /> Update Profile
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </DefaultLayout>
    );
}
